// @ts-nocheck
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  TableProvider,
  useTableDispatch,
  useTableState,
} from './context/TableSchemaBuilderContext';
import TableGrid from './components/TableGrid/TableGrid';
import Toolbar from './components/Toolbar/Toolbar';
import CellPreviewer from './components/CellPreviewer/CellPreviewer';
import FormulaEditorPanel from './components/Toolbar/Formula/FormulaEditorPanel';
import TableEventBoundary from './components/TableEventBoundary';
import useAttributes from '../../hooks/Configuration/AttributeLibrary/useAttributes';
import ConfigColumnBuilder from './components/ConfigColumn/ConfigColumnBuilder';

const DefaultObject = {};
const DefaultFunction = () => {};

const TableSchemaContent = ({
  initialSchema = DefaultObject,
  onSchemaChange = DefaultFunction,
  tableConfig = DefaultObject,
  attributeList: externalAttributeList,
  sx = {},
}) => {
  const { schema, config } = useTableState();
  const dispatch = useTableDispatch();
  const { getAllAttributeList } = useAttributes();
  const [attributeList, setAttributeList] = useState([]);

  // Memoize the merged config to prevent flash of default config
  const mergedConfig = useMemo(() => {
    return {
      ...config,
      ...tableConfig,
    };
  }, [config, tableConfig]);

  const isConfigColumnMode = mergedConfig?.configColumn || false;

  // Handle initial schema load
  useEffect(() => {
    if (initialSchema) {
      dispatch({ type: 'SET_SCHEMA', payload: initialSchema });
    }
  }, [initialSchema]);

  // Handle schema changes
  useEffect(() => {
    if (schema) {
      onSchemaChange(schema);
    }
  }, [schema]);

  // Handle config changes - set config immediately
  useEffect(() => {
    if (tableConfig && Object.keys(tableConfig).length > 0) {
      dispatch({ type: 'SET_CONFIG', payload: tableConfig });
    }
  }, [tableConfig]);

  // This function is used to fetch all attributes from the API
  const getAttributes = useCallback(() => {
    getAllAttributeList().then((res) => {
      if (res) {
        setAttributeList(res?.data || []);
      }
    });
  }, [getAllAttributeList]);

  useEffect(() => {
    if (mergedConfig?.fromConstAttributeFeature === false) {
      getAttributes();
    }
  }, [mergedConfig?.fromConstAttributeFeature]);

  // Use external attribute list if provided (from DefineSchemaModal), fallback to fetched
  const resolvedAttributeList =
    externalAttributeList?.length > 0 ? externalAttributeList : attributeList;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '380px',
        position: 'relative',
        overflow: 'visible',
        ...sx,
      }}
    >
      {/* Toolbar + CellPreviewer only in normal (non-configColumn) mode */}
      {mergedConfig?.showToolbar && !isConfigColumnMode && <Toolbar />}
      {!isConfigColumnMode && (
        <TableEventBoundary>
          {mergedConfig?.showCellPreviewer && <CellPreviewer />}
        </TableEventBoundary>
      )}

      {/* Config Column mode: sidebar + detail panel layout */}
      {isConfigColumnMode ? (
        <ConfigColumnBuilder attributeList={resolvedAttributeList} />
      ) : (
        /* Normal spreadsheet mode */
        <TableEventBoundary>
          <Box sx={{ width: '100%', height: '100%' }}>
            <TableGrid />
          </Box>
        </TableEventBoundary>
      )}

      {/* Formula editor only in normal mode */}
      {!isConfigColumnMode && (
        <FormulaEditorPanel attributeList={resolvedAttributeList} />
      )}
    </Box>
  );
};

const TableSchemaBuilder = ({
  initialSchema = DefaultObject,
  onSchemaChange = DefaultFunction,
  tableConfig = DefaultObject,
  attributeList = [],
  sx = {},
}) => {
  return (
    <TableProvider>
      <TableSchemaContent
        onSchemaChange={onSchemaChange}
        initialSchema={initialSchema}
        tableConfig={tableConfig}
        attributeList={attributeList}
        sx={sx}
      />
    </TableProvider>
  );
};

TableSchemaContent.propTypes = {
  initialSchema: PropTypes.shape({}),
  onSchemaChange: PropTypes.func,
  tableConfig: PropTypes.shape({}),
  attributeList: PropTypes.arrayOf(PropTypes.shape({})),
  sx: PropTypes.shape({}),
};

TableSchemaBuilder.propTypes = {
  initialSchema: PropTypes.shape({}),
  onSchemaChange: PropTypes.func,
  tableConfig: PropTypes.shape({}),
  attributeList: PropTypes.arrayOf(PropTypes.shape({})),
  sx: PropTypes.shape({}),
};

export default TableSchemaBuilder;
